import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faCalendarDays,
  faCartShopping,
  faMedal,
  faSeedling,
  faTag,
} from '@fortawesome/pro-regular-svg-icons';
import type { AutomationType } from '@playbook-technologies/common-enums';

import { keys } from '@client/new/helpers/general';

export type AutomationConfigField =
  | 'firstWorkout'
  | 'otpPitchTargetProductId'
  | 'includePushes';

/**
 * Only what the server does not own. Names, descriptions, cadence and trigger
 * lines come from `GET /creator/automations`; this map adds the icon, the
 * money-line label and admin-tool details per type.
 */
/** Which number headlines the funnel card and how the first and last rows are called. */
export type InsightsCopy = {
  enrolledLabel: string;
  /** Names the converted row and headlines the card with the type's own verb
   *  ("12 Checked out"). Null hides both, for a type with no goal to convert
   *  on (GRW-887). No money anywhere: GMV is inflated against the Hex
   *  definitions (GRW-783). */
  convertedLabel: string | null;
};

/** Noun for the list's green line: "12 recovered carts to date". */
export type ConversionNoun = { one: string; many: string };

type AutomationCopy = {
  /** Admin tools (logs filter, disable dialog) name the type; the list uses the server's displayName. */
  label: string;
  icon: IconProp;
  /** Absent hides the conversion line on the list row. */
  conversionNoun?: ConversionNoun;
  insights: InsightsCopy;
  /** Send-test copy variants; empty when the sequence has a single variant. */
  variants: readonly string[];
  /** Mirrors creator-api `allowedConfigKeys`: which jsonb config keys the
   *  type reads. Anything else is rejected by the server with a 400. */
  configFields: readonly AutomationConfigField[];
  /** Keys the server's enable gate demands (mirrors `enableRequirements`);
   *  Save stays disabled while one is empty and the switch is on. */
  requiredConfigFields: readonly AutomationConfigField[];
  /** The server gates enabling on data it owns (prices, price paths), so
   *  the row shows a readiness line instead of a config field. */
  serverReadinessCheck: boolean;
  /** What a creator sees while the type is `needs_setup`: the list tooltip,
   *  the details hint when no form can fix it. The server's `readinessReason`
   *  is developer text and is never rendered (GRW-821). */
  setupHint: string;
  /** The sequence exits on a purchase goal, so a buyer stops getting the
   *  rest. False for a flow with no goal exit (workout_congrats). */
  exitsOnPurchase: boolean;
  /** The Help Center section that shows every message of the sequence in
   *  full (GRW-909). Intercom minted the anchors; they survive edits but
   *  not deleting and re-adding the heading. */
  helpArticleUrl: string;
};

const HELP_ARTICLE_URL =
  'https://help.playbookapp.io/en/articles/17219817-user-lifecycle-automations';

export const AUTOMATION_COPY = {
  abandoned_cart: {
    label: 'Abandoned cart emails',
    icon: faCartShopping,
    conversionNoun: { one: 'recovered cart', many: 'recovered carts' },
    insights: {
      enrolledLabel: 'Carts abandoned',
      convertedLabel: 'Checked out',
    },
    variants: ['subscription', 'otp', 'offer'],
    configFields: [],
    requiredConfigFields: [],
    serverReadinessCheck: false,
    setupHint: 'Open this automation to finish setting it up.',
    exitsOnPurchase: true,
    helpArticleUrl: `${HELP_ARTICLE_URL}#h_cd85cbe441`,
  },
  trial_conversion: {
    label: 'Trial conversion',
    icon: faSeedling,
    conversionNoun: { one: 'new subscriber', many: 'new subscribers' },
    insights: {
      enrolledLabel: 'Trials started',
      convertedLabel: 'Subscribed',
    },
    variants: [],
    configFields: ['firstWorkout', 'includePushes'],
    requiredConfigFields: ['firstWorkout'],
    serverReadinessCheck: false,
    setupHint:
      'Pick the workout new trial members should start with, then switch it on.',
    exitsOnPurchase: true,
    helpArticleUrl: `${HELP_ARTICLE_URL}#h_1aa8a168c3`,
  },
  otp_to_subscription: {
    label: 'One-time purchase to subscription',
    icon: faTag,
    conversionNoun: { one: 'new subscriber', many: 'new subscribers' },
    insights: {
      enrolledLabel: 'One-time buyers',
      convertedLabel: 'Subscribed',
    },
    variants: [],
    configFields: ['otpPitchTargetProductId', 'includePushes'],
    requiredConfigFields: [],
    serverReadinessCheck: false,
    setupHint:
      'Choose which subscription the messages should point at, then switch it on.',
    exitsOnPurchase: true,
    helpArticleUrl: `${HELP_ARTICLE_URL}#h_159a125b33`,
  },
  monthly_to_annual: {
    label: 'Monthly to annual',
    icon: faCalendarDays,
    conversionNoun: { one: 'annual upgrade', many: 'annual upgrades' },
    insights: {
      enrolledLabel: 'Monthly members reached',
      convertedLabel: 'Switched to annual',
    },
    variants: [],
    configFields: ['includePushes'],
    requiredConfigFields: [],
    serverReadinessCheck: true,
    setupHint:
      'Add both a monthly and an annual price to your subscription, then switch it on.',
    exitsOnPurchase: true,
    helpArticleUrl: `${HELP_ARTICLE_URL}#h_8f78ea0962`,
  },
  workout_congrats: {
    label: 'Workout congratulations',
    icon: faMedal,
    insights: {
      enrolledLabel: 'First workouts',
      convertedLabel: null,
    },
    variants: [],
    configFields: [],
    requiredConfigFields: [],
    serverReadinessCheck: false,
    setupHint: 'Open this automation to finish setting it up.',
    exitsOnPurchase: false,
    helpArticleUrl: `${HELP_ARTICLE_URL}#h_d6c8d0c358`,
  },
} satisfies Record<AutomationType, AutomationCopy>;

export const AUTOMATION_ORDER = keys(AUTOMATION_COPY);

export const isKnownAutomationType = (value: string): value is AutomationType =>
  Object.prototype.hasOwnProperty.call(AUTOMATION_COPY, value);

/** A type the frontend has never heard of still renders: generic icon, no
 *  conversion line, no admin config fields, generic disable dialog. */
const FALLBACK_COPY: AutomationCopy = {
  label: 'this automation',
  icon: faBolt,
  insights: {
    enrolledLabel: 'Enrolled',
    convertedLabel: 'Converted',
  },
  variants: [],
  configFields: [],
  requiredConfigFields: [],
  serverReadinessCheck: false,
  setupHint: 'Open this automation to finish setting it up.',
  exitsOnPurchase: false,
  helpArticleUrl: HELP_ARTICLE_URL,
};

export const copyFor = (automationType: string): AutomationCopy =>
  isKnownAutomationType(automationType)
    ? AUTOMATION_COPY[automationType]
    : FALLBACK_COPY;

/** Portal owns section titles and order (GRW-530); the server sends the key. */
export const SECTION_COPY = {
  convert_new_signups: {
    title: 'Convert new signups',
    subtitle:
      'Nudges for people who started signing up or are inside a free trial.',
  },
  grow_revenue: {
    title: 'Grow revenue per member',
    subtitle: 'Offers that move members onto a bigger or longer plan.',
  },
  build_the_habit: {
    title: 'Build the habit',
    subtitle: 'Encouragement that keeps members coming back.',
  },
} as const satisfies Record<string, { title: string; subtitle: string }>;

export const SECTION_ORDER = keys(SECTION_COPY);
